import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Clock, Download, Music, Send, Zap, AlertTriangle, CheckCircle } from "lucide-react";

interface TimingStats {
  stage: string;
  avg_duration_ms: number;
  min_duration_ms: number;
  max_duration_ms: number;
  count: number;
  recent_avg_ms: number;
  is_slow: boolean;
}

interface RecentTiming {
  id: string;
  generation_id: string;
  stage: string;
  timestamp: string;
  duration_ms: number | null;
  metadata: Record<string, unknown> | null;
}

const stageLabels: Record<string, string> = {
  'lyrics_generated': '🥣 Текст готов (Claude)',
  'music_started': '🔥 Духовка разогрета',
  'suno_api_called': '📋 Заказ принят (taskId)',
  'music_callback_received': '🍳 Готовится на кухне',
  'demos_uploaded': '🍽️ Блюдо на тарелке',
  'telegram_demos_sent': '🎂 Подано гостю',
};

const stageIcons: Record<string, React.ReactNode> = {
  'lyrics_generated': <Zap className="w-4 h-4" />,
  'music_started': <Music className="w-4 h-4" />,
  'suno_api_called': <Zap className="w-4 h-4" />,
  'music_callback_received': <Clock className="w-4 h-4" />,
  'demos_uploaded': <Download className="w-4 h-4" />,
  'telegram_demos_sent': <Send className="w-4 h-4" />,
};

const SLOW_THRESHOLD = 1.5;

const formatDuration = (ms: number | null): string => {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}мс`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}с`;
  return `${(ms / 60000).toFixed(1)}мин`;
};

export default function Timings() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['timing-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generation_timings')
        .select('stage, duration_ms, timestamp')
        .not('duration_ms', 'is', null)
        .order('timestamp', { ascending: false });
      
      if (error) throw error;
      
      const byStage: Record<string, { all: number[]; recent: number[] }> = {};
      
      for (const row of data || []) {
        if (!byStage[row.stage]) byStage[row.stage] = { all: [], recent: [] };
        if (row.duration_ms !== null) {
          byStage[row.stage].all.push(row.duration_ms);
          if (byStage[row.stage].recent.length < 5) {
            byStage[row.stage].recent.push(row.duration_ms);
          }
        }
      }
      
      const result: TimingStats[] = [];
      for (const [stage, { all, recent }] of Object.entries(byStage)) {
        if (all.length === 0) continue;
        
        const avgAll = all.reduce((a, b) => a + b, 0) / all.length;
        const avgRecent = recent.length > 0 
          ? recent.reduce((a, b) => a + b, 0) / recent.length 
          : avgAll;
        
        result.push({
          stage,
          avg_duration_ms: Math.round(avgAll),
          min_duration_ms: Math.min(...all),
          max_duration_ms: Math.max(...all),
          count: all.length,
          recent_avg_ms: Math.round(avgRecent),
          is_slow: avgRecent > avgAll * SLOW_THRESHOLD && all.length >= 5,
        });
      }
      
      return result.sort((a, b) => {
        const order = ['lyrics_generated', 'music_started', 'suno_api_called', 'music_callback_received', 'demos_uploaded', 'telegram_demos_sent'];
        return order.indexOf(a.stage) - order.indexOf(b.stage);
      });
    },
  });

  const { data: recentTimings, isLoading: recentLoading } = useQuery({
    queryKey: ['recent-timings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generation_timings')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data as RecentTiming[];
    },
  });

  const groupedByGeneration = recentTimings?.reduce((acc, timing) => {
    if (!acc[timing.generation_id]) acc[timing.generation_id] = [];
    acc[timing.generation_id].push(timing);
    return acc;
  }, {} as Record<string, RecentTiming[]>) || {};

  const slowStages = stats?.filter(s => s.is_slow) || [];
  const hasAlerts = slowStages.length > 0;

  return (
    <AdminLayout
      title="⏱️ Аналитика времени"
      description="Статистика времени выполнения каждого этапа генерации"
    >
      {/* Alerts Section */}
      {!statsLoading && stats && stats.length > 0 && (
        <div className="mb-6">
          {hasAlerts ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Обнаружены замедления</AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-1">
                  {slowStages.map(stage => (
                    <li key={stage.stage} className="flex items-center gap-2">
                      {stageIcons[stage.stage]}
                      <span className="font-medium">{stageLabels[stage.stage]}</span>
                      <span className="text-muted-foreground">
                        — последние 5: <span className="text-destructive font-mono">{formatDuration(stage.recent_avg_ms)}</span>
                        {' '}vs обычно: <span className="font-mono">{formatDuration(stage.avg_duration_ms)}</span>
                        {' '}(+{Math.round((stage.recent_avg_ms / stage.avg_duration_ms - 1) * 100)}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-primary/50 bg-primary/5">
              <CheckCircle className="h-4 w-4 text-primary" />
              <AlertTitle>Всё в норме</AlertTitle>
              <AlertDescription>
                Все этапы работают в пределах обычных показателей
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 mb-6">
        {statsLoading ? (
          Array(5).fill(0).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))
        ) : stats && stats.length > 0 ? (
          stats.map((stat) => (
            <Card key={stat.stage} className={stat.is_slow ? 'border-destructive bg-destructive/5' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {stageIcons[stat.stage]}
                  {stageLabels[stat.stage] || stat.stage}
                  {stat.is_slow && <AlertTriangle className="w-3 h-3 text-destructive" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatDuration(stat.avg_duration_ms)}</div>
                {stat.is_slow && (
                  <p className="text-xs text-destructive mt-1">
                    Последние 5: {formatDuration(stat.recent_avg_ms)}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  min: {formatDuration(stat.min_duration_ms)} / max: {formatDuration(stat.max_duration_ms)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stat.count} измерений
                </p>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="col-span-full">
            <CardContent className="pt-6 text-center text-muted-foreground">
              Нет данных о времени выполнения. Создайте песню для начала сбора статистики.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Generations Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Последние генерации</CardTitle>
        </CardHeader>
        <CardContent>
          {recentLoading ? (
            <div className="space-y-4">
              {Array(3).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : Object.keys(groupedByGeneration).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedByGeneration).slice(0, 10).map(([genId, timings]) => {
                const sortedTimings = timings.sort((a, b) => 
                  new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                );
                const firstTimestamp = new Date(sortedTimings[0]?.timestamp);
                
                return (
                  <div key={genId} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <code className="text-xs text-muted-foreground">{genId.slice(0, 8)}...</code>
                      <span className="text-xs text-muted-foreground">
                        {firstTimestamp.toLocaleString('ru-RU')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {sortedTimings.map((timing) => (
                        <div
                          key={timing.id}
                          className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm"
                          title={JSON.stringify(timing.metadata, null, 2)}
                        >
                          {stageIcons[timing.stage]}
                          <span>{stageLabels[timing.stage] || timing.stage}</span>
                          {timing.duration_ms && (
                            <span className="font-mono text-xs text-primary ml-1">
                              {formatDuration(timing.duration_ms)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Нет данных. Создайте песню в боте для начала сбора статистики.
            </p>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
